/* Pexar YouTube · mounts a player that actually starts playing.

   `?autoplay=1` on an <iframe> src is a request, not an instruction. Chrome and Safari
   refuse autoplay WITH SOUND unless the embedding site has earned enough media
   engagement, and a site nobody has watched video on yet - a preview theme, a new
   visitor - has none. The browser denies it and YouTube falls back to its own poster
   and its own red play button, so the shopper has to press play a second time, on a
   thumbnail that is not even the cover image the merchant chose.

   No API can overrule that - playVideo() is refused the same way the URL parameter is.
   The one thing the browser always permits is MUTED playback, so the player asks for
   sound first and falls back to muted only when it has to:

     onReady -> playVideo()
     +1.2s   -> still not playing? mute() and playVideo() again

   A browser that allows sound-on autoplay never reaches the second line and the shopper
   hears the video on one click. A browser that does not gets moving pictures on that
   same click instead of a still poster, and YouTube's own speaker button - top right of
   the embed - turns the sound on. No control of ours is needed for that; adding one
   would just sit next to YouTube's.

   The API script is only fetched on the first play. Nothing here runs, and nothing is
   downloaded from Google, until someone asks to watch something.

   Only one video plays at a time. The Gallery Wall plays inline on the page while the
   Recent Reels play in a lightbox on top of it, so without a registry the shopper opens
   a reel and hears two soundtracks at once. Every player this module makes is recorded,
   and mounting a new one - or opening the lightbox, which calls pauseAll - stops the
   rest first. Players whose iframe has left the document are dropped on the way past.

   Usage:  PexarYouTube.mount(container, videoId, title)
           - container is emptied and the player is put inside it
           - size the player from the container; the iframe is 100% x 100%
           PexarYouTube.pauseAll()
           - stop everything, e.g. before something else starts making noise */
window.PexarYouTube = (function () {
  var ALLOW = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';

  /* How long to wait for the browser to say yes before offering it a muted video
     instead. Generous on purpose: a slow start on a slow connection is not a refusal,
     and muting a video that was about to play with sound is the worse mistake. The
     deadline is cancelled the moment the player reports it is running, so a healthy
     start never waits this long. */
  var SOUND_GRACE = 2500;

  var apiPromise = null;

  /* Everything currently able to make noise. Entries are either an API player or, when
     the API could not be reached, the bare iframe we fell back to. */
  var playing = [];

  function element(entry) {
    if (entry.player) {
      return entry.player.getIframe ? entry.player.getIframe() : null;
    }
    return entry.frame;
  }

  function pause(entry) {
    if (entry.player) {
      if (typeof entry.player.pauseVideo === 'function') entry.player.pauseVideo();
      return;
    }
    /* The fallback iframe carries enablejsapi=1 precisely so this works: the embed
       listens for these commands itself, with no script of ours involved. */
    var frame = entry.frame;
    if (frame && frame.contentWindow) {
      frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    }
  }

  function pauseAll(keep) {
    for (var i = playing.length - 1; i >= 0; i--) {
      var entry = playing[i];
      var el = element(entry);
      /* Closing the lightbox empties it, so its player is gone but still listed. */
      if (!el || !document.contains(el)) {
        playing.splice(i, 1);
        continue;
      }
      if (entry === keep) continue;
      pause(entry);
    }
  }

  function loadApi() {
    if (apiPromise) return apiPromise;

    apiPromise = new Promise(function (resolve, reject) {
      if (window.YT && window.YT.Player) {
        resolve(window.YT);
        return;
      }

      /* onYouTubeIframeAPIReady is a single global the API calls exactly once. Another
         script on the page may already own it, so chain rather than clobber. */
      var previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof previous === 'function') previous();
        resolve(window.YT);
      };

      var script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onerror = function () {
        reject(new Error('iframe_api blocked'));
      };
      document.head.appendChild(script);
    });

    return apiPromise;
  }

  /* What we did before the API existed, and still the right answer when the API cannot
     be reached at all - an ad blocker, a corporate proxy, a dead network. The shopper
     gets YouTube's poster and one more click, which beats an empty box. */
  function plainIframe(container, videoId, title) {
    var frame = document.createElement('iframe');
    frame.src =
      'https://www.youtube-nocookie.com/embed/' +
      encodeURIComponent(videoId) +
      '?autoplay=1&rel=0&playsinline=1&enablejsapi=1';
    frame.title = title || 'YouTube video';
    frame.allow = ALLOW;
    frame.allowFullscreen = true;
    container.replaceChildren(frame);
    playing.push({ frame: frame });
  }

  function mount(container, videoId, title) {
    if (!container || !videoId) return;

    /* Whatever was playing stops before the new one starts, not after it - two
       soundtracks overlapping for even a moment is the bug this exists to prevent. */
    pauseAll();

    /* The API replaces this element with its iframe, so it has to be in the document
       before the player is constructed. */
    var holder = document.createElement('div');
    container.replaceChildren(holder);

    /* Cleared by the first onStateChange that says the video is running. Polling the
       state once at a fixed moment would call a slow start a refusal. */
    var deadline = null;
    function settled() {
      if (deadline) {
        clearTimeout(deadline);
        deadline = null;
      }
    }

    loadApi()
      .then(function (YT) {
        new YT.Player(holder, {
          host: 'https://www.youtube-nocookie.com',
          videoId: videoId,
          width: '100%',
          height: '100%',
          playerVars: { autoplay: 1, rel: 0, playsinline: 1 },
          events: {
            onReady: function (event) {
              var player = event.target;
              var entry = { player: player };
              playing.push(entry);
              /* The click that mounted this may have raced another player into life. */
              pauseAll(entry);

              var frame = player.getIframe();
              if (frame) {
                if (title) frame.title = title;
                /* The API's own allow list includes web-share, which desktop Chrome
                   does not implement and logs about. Rewriting it here does not undo
                   the log - the container policy was fixed when the frame navigated -
                   but it keeps the attribute honest for anything reading the DOM. */
                frame.setAttribute('allow', ALLOW);
              }

              player.playVideo();

              deadline = setTimeout(function () {
                deadline = null;
                /* Still not running after all that: the browser refused, and the only
                   offer it will accept is a muted one. */
                var state = player.getPlayerState();
                if (state === 1 || state === 3) return;
                player.mute();
                player.playVideo();
              }, SOUND_GRACE);
            },

            /* PLAYING and BUFFERING both mean the browser said yes, whenever they
               arrive. Nothing needs muting after that. */
            onStateChange: function (event) {
              if (event.data === 1 || event.data === 3) settled();
            }
          }
        });
      })
      .catch(function () {
        plainIframe(container, videoId, title);
      });
  }

  return {
    mount: mount,
    pauseAll: function () {
      pauseAll();
    }
  };
})();
